import { Category } from '@/data/mockData';

export interface ApiEstimationRow {
  name: string;
  type: string;
  volume_email: number;
  volume_phone: number;
  volume_email_or_phone: number;
  volume_email_and_phone: number;
}

export interface ApiEstimationVolumes {
  [categoryName: string]: ApiEstimationRow[];
}

/**
 * Volume d'UNE population (un total agrégé tous segments confondus, logique « All »).
 * Forme miroir du contrat API `population_volumes` (cf. .agents/contract.md §2).
 */
export interface PopulationVolumeItem {
  volume_email: number;
  volume_phone: number;
  volume_email_or_phone: number;
  volume_email_and_phone: number;
}

/**
 * Volumes d'estimation par population : UN TOTAL PAR POPULATION (plus de matrice
 * population×segment). Forme miroir du contrat API `population_volumes` :
 *   population_volumes[population] = PopulationVolumeItem
 * (cf. .agents/contract.md §2).
 */
export interface ApiPopulationVolumes {
  [population: string]: PopulationVolumeItem;
}

/**
 * Normalise une chaîne pour la comparaison (supprime accents, normalise casse)
 */
export const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

/**
 * Mappe la réponse d'estimation de l'API vers les volumes des segments.
 * Pour l'instant, on ignore la notion de durée et on applique les mêmes volumes
 * pour 30 / 60 / 90 jours.
 */
export const mapApiEstimationToSegmentVolumes = (
  categories: Category[],
  estimationVolumes?: ApiEstimationVolumes
): Category[] => {
  if (!estimationVolumes) {
    return categories;
  }

  return categories.map((category) => {
    // Chercher la catégorie dans estimationVolumes avec matching insensible à la casse/accents
    // Ignorer "All" car c'est pour les totaux, pas pour les catégories
    const categoryKey = Object.keys(estimationVolumes)
      .filter(key => key !== 'All')
      .find(
        (key) => normalizeString(key) === normalizeString(category.name)
      );

    if (!categoryKey) {
      return category;
    }

    const rows = estimationVolumes[categoryKey];
    if (!rows || rows.length === 0) {
      return category;
    }

    const updatedSegments = category.segments.map((segment) => {
      const matchedRow = rows.find(
        (row) => normalizeString(row.name) === normalizeString(segment.name)
      );

      if (!matchedRow) {
        return segment;
      }

      const baseVolume = {
        email: matchedRow.volume_email,
        mobile: matchedRow.volume_phone,
        emailOrMobile: matchedRow.volume_email_or_phone,
        emailAndMobile: matchedRow.volume_email_and_phone,
      };

      return {
        ...segment,
        volumes: {
          '30': baseVolume,
          '60': baseVolume,
          '90': baseVolume,
        },
      };
    });

    return {
      ...category,
      segments: updatedSegments,
    };
  });
};

/**
 * Retrouve le volume agrégé d'une population donnée dans `population_volumes`,
 * avec un matching insensible à la casse / aux accents.
 */
export const findPopulationVolumes = (
  populationVolumes: ApiPopulationVolumes | undefined,
  population: string
): PopulationVolumeItem | undefined => {
  if (!populationVolumes) {
    return undefined;
  }
  const key = Object.keys(populationVolumes).find(
    (k) => normalizeString(k) === normalizeString(population)
  );
  return key ? populationVolumes[key] : undefined;
};

/** Totaux dédupliqués (union) lus depuis la catégorie "All" d'un jeu de volumes. */
export interface VolumeTotalsFromAll {
  email: number;
  mobile: number;
  emailOrMobile: number;
  emailAndMobile: number;
}

/**
 * Extrait les totaux à partir de la catégorie "All" de `estimation_volumes`
 * (global, par segment). "All" est l'union dédupliquée tous segments confondus :
 * même sémantique que le total global, et elle évite le sur-comptage d'une simple
 * somme segment-par-segment. Retourne `undefined` si "All" est absent (l'appelant
 * peut alors retomber sur une somme des segments).
 */
export const extractTotalsFromAll = (
  volumes: ApiEstimationVolumes | undefined
): VolumeTotalsFromAll | undefined => {
  if (!volumes) {
    return undefined;
  }
  const allCategory = volumes['All'];
  if (!allCategory || allCategory.length === 0) {
    return undefined;
  }
  const allRow = allCategory[0];
  return {
    email: allRow.volume_email || 0,
    mobile: allRow.volume_phone || 0,
    emailOrMobile: allRow.volume_email_or_phone || 0,
    emailAndMobile: allRow.volume_email_and_phone || 0,
  };
};

/**
 * Convertit le total agrégé d'une population (`PopulationVolumeItem`, déjà union
 * dédupliquée côté API) vers la forme d'affichage `VolumeTotals`.
 * Retourne `undefined` si la population est absente de `population_volumes`.
 */
export const extractPopulationTotals = (
  item: PopulationVolumeItem | undefined
): VolumeTotalsFromAll | undefined => {
  if (!item) {
    return undefined;
  }
  return {
    email: item.volume_email || 0,
    mobile: item.volume_phone || 0,
    emailOrMobile: item.volume_email_or_phone || 0,
    emailAndMobile: item.volume_email_and_phone || 0,
  };
};


